import { config } from '@uhg-abyss/web/tools/config';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';

import { useLagoon } from '../../hooks/useLagoon';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { ConstantsLagoon } from '../ConstantsLagoon';
import {
  CareProviderContainer,
  CareProviderImage,
  CareTeamEmptyCardContainer,
  CareTeamEmptyCardData,
  CareTeamEmptyCardDescription,
  CareTeamEmptyCardHeader,
  CareTeamEmptyCardHeading,
  CareTeamEmptyCardStyle,
  CareTeamSkeleton,
} from './CareTeamEmptyState.styled';

const provider = [
  {
    name: 'John Doctor, MD',
    bookMarked: true,
    img: `${config(
      'CDN_BASE_URL'
    )}/cdn/assets/images/default_Practitioner1.png`,
  },
  {
    bookMarked: false,
    name: 'Sandra Physician, DO',
    img: `${config(
      'CDN_BASE_URL'
    )}/cdn/assets/images/default_Practitioner2.png`,
  },
];
export interface CareTeamEmptyCardProps {
  filteredPcpConfig?: {
    showCurrentPcpCard?: boolean;
    showFuturePcpCard?: boolean;
    [key: string]: any;
  };
}

export const CareTeamEmptyCard: React.FC<CareTeamEmptyCardProps> = ({
  filteredPcpConfig,
}) => {
  const lagoonData = useLagoon('ui-messaging')();
  const isGEHAV2 = shouldUseGehaV2Styling();
  let cardDescription: { title?: string; message?: string } = {};
  let pcpStatus: 'both' | 'none' | undefined = undefined;

  cardDescription = Array.isArray(lagoonData)
    ? find(
        lagoonData,
        (item: any) =>
          item.key ===
          ConstantsLagoon.UI_MESSAGING.CARE_TEAM.CARE_EMPTY_CARD_DESCRIPTION
      ) || {}
    : {};

  // Config-driven: use filteredPcpConfig for design logic
  const showCurrent = !!filteredPcpConfig?.showCurrentPcpCard;
  const showFuture = !!filteredPcpConfig?.showFuturePcpCard;
  if (showCurrent && showFuture) {
    pcpStatus = 'both';
  } else if (!showCurrent && !showFuture) {
    pcpStatus = 'none';
  } else {
    pcpStatus = undefined; // Only one PCP
  }

  return (
    <CareTeamEmptyCardContainer
      alignContent="center"
      alignItems="center"
      data-auto-testid="care-team-empty-card"
      data-testid="care-team-empty-card"
      pcpStatus={pcpStatus}
    >
      <CareTeamEmptyCardStyle pcpStatus={pcpStatus}>
        <CareTeamEmptyCardHeader pcpStatus={pcpStatus}>
          <CareTeamEmptyCardHeading
            data-auto-testid="care-team-empty-header"
            data-testid="care-team-empty-header"
            level={1}
            pcpStatus={pcpStatus}
            size="md"
          >
            {cardDescription?.title}
          </CareTeamEmptyCardHeading>
          <CareTeamEmptyCardDescription
            data-auto-testid="care-team-empty-description"
            data-testid="care-team-empty-description"
            pcpStatus={pcpStatus}
            size={pcpStatus === 'both' ? undefined : '$md'}
          >
            {cardDescription?.message}
          </CareTeamEmptyCardDescription>
        </CareTeamEmptyCardHeader>
        <CareTeamEmptyCardData>
          {provider.map((item, index) => (
            <CareProviderContainer key={index}>
              <IconSymbol
                color="$core.color.brand.120"
                data-auto-testid="sub-Specialities-drawer"
                data-testid="sub-Specialities-drawer"
                icon="bookmark"
                size="24px"
                variant={item.bookMarked ? 'filled' : 'outlined'}
              />
              <CareProviderImage data-testid={`care-provider-image`}>
                <img alt="img" src={item.img} style={{ borderRadius: '50%' }} />
              </CareProviderImage>
              <div>
                <Text
                  color={isGEHAV2 ? '$core.color.brand.100' : '$blue'}
                  data-auto-testid="care-team-empty-text"
                  data-testid="care-team-empty-text"
                  fontSize="md"
                  fontWeight="bold"
                >
                  {item.name}
                </Text>
                <CareTeamSkeleton
                  data-auto-testid="care-team-skeleton"
                  data-testid="care-team-skeleton"
                  size="md"
                />
                <CareTeamSkeleton
                  data-auto-testid="care-team-skeleton"
                  data-testid="care-team-skeleton"
                  size="lg"
                />
                <CareTeamSkeleton
                  data-auto-testid="care-team-skeleton"
                  data-testid="care-team-skeleton"
                  size="lg"
                />
              </div>
            </CareProviderContainer>
          ))}
        </CareTeamEmptyCardData>
      </CareTeamEmptyCardStyle>
    </CareTeamEmptyCardContainer>
  );
};
